/**
 * Workflows editing card (P4.T5).
 *
 * Replaces the T1 placeholder body. Renders one row per installed
 * workflow binding sorted by workflow_id with:
 *   - workflow name (from catalog) + workflow_id (mono) + description
 *   - inline channel <select> (CHANGE-CHANNEL affordance — calls
 *     composerStore.setWorkflowChannel; preserves unknown fields via
 *     spread)
 *   - remove button (UNBIND affordance — composerStore.removeWorkflow)
 *
 * "+ Add workflow" button at the bottom opens WorkflowPickerModal
 * (ADD affordance). Modal = ADD, Select = CHANGE-CHANNEL, X = UNBIND —
 * three disjoint affordances (matches T4's pattern).
 *
 * Reads:
 *   - draftBundle ?? serverBundle for installed bindings
 *   - useWorkflows (P4.T5) for workflow name + description display lookup
 *
 * Writes via composerStore.removeWorkflow + setWorkflowChannel. Save
 * flows through the Workshop top bar (T2) →
 * useUpdatePersonaBundle (T2, widened in T5 to send workflow_bindings).
 *
 * Catalog-load failure: card still renders installed bindings (with
 * bare workflow_id labels — no display lookup); inline Alert at the
 * top surfaces the error with a Retry button; **inline channel Select
 * stays enabled** because the channel options are static (don't
 * depend on the workflow catalog).
 *
 * No workflow-authoring affordance — "publish" in the ticket title
 * refers to the Workshop top bar's Save flow (publish the binding to
 * the persona). Workflow authoring lives on a workspace-authoring
 * surface (deferred to a future ticket). See plan §5 pins.
 */
import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { X } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import {
  useComposerStore,
  type WorkflowBindingValue,
  type WorkflowChannel,
} from '@/stores/composerStore'
import { useWorkflows, type WorkflowSummary } from '@/hooks/useWorkflows'
import { useBundleFindings } from '@/hooks/useBundleFindings'
import { useFeature } from '@/hooks'
import { TrafficLight } from '@/components/composer/TrafficLight'
import { ExplainPopover } from '@/components/composer/ExplainPopover'
import type { Finding, FindingLocation } from '@/utils/bundleFindings'
import { WorkflowPickerModal } from './WorkflowPickerModal'
import { WorkflowTestDrawer } from './WorkflowTestDrawer'

const CHANNEL_OPTIONS: WorkflowChannel[] = ['*', 'whatsapp', 'chat', 'voice']

// SA-474: `*` and `chat` both take over web chat, so their labels say so.
const CHANNEL_LABELS: Record<WorkflowChannel, string> = {
  '*': '* (any channel, including web chat)',
  whatsapp: 'whatsapp',
  chat: 'chat (web chat)',
  voice: 'voice',
}

interface BindingRowProps {
  binding: WorkflowBindingValue
  workflow: WorkflowSummary | undefined
  finding: Finding | undefined
  onJump?: (location: FindingLocation) => void
  showTestButton?: boolean
  onTest?: (workflowId: string, workflowName: string) => void
}

function BindingRow({ binding, workflow, finding, onJump, showTestButton, onTest }: BindingRowProps) {
  const setWorkflowChannel = useComposerStore((s) => s.setWorkflowChannel)
  const removeWorkflow = useComposerStore((s) => s.removeWorkflow)
  const showOverviewLink = useFeature('workflow_transparency')

  // P4.T9: workflow-card findings (rule 6 = workflow_uses_unbound_capability)
  // jump to the Tools card so the user can bind the missing capability.
  const jumpTo: FindingLocation | undefined =
    finding && finding.rule === 'workflow_uses_unbound_capability'
      ? { card: 'tools', anchorId: '' }
      : undefined

  return (
    <li className="rounded-sm border border-border p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            {workflow ? (
              <span className="text-sm font-semibold text-foreground">
                {workflow.name}
              </span>
            ) : null}
            <span className="font-mono text-xs text-muted-foreground">
              {binding.workflow_id}
            </span>
            {showOverviewLink && (
              <Link
                to={`/workflows/${binding.workflow_id}`}
                className="text-xs text-primary hover:underline"
                aria-label={`How ${binding.workflow_id} works`}
              >
                How it works
              </Link>
            )}
            {finding && (
              <ExplainPopover
                title={finding.message}
                reasoning={finding.explanation}
                jumpTo={jumpTo}
                onJump={onJump}
              >
                <button
                  type="button"
                  className="cursor-pointer ml-1 align-middle"
                  aria-label={`Explain finding for ${binding.workflow_id}`}
                >
                  <TrafficLight severity={finding.severity} />
                </button>
              </ExplainPopover>
            )}
            {workflow?.languages?.length ? (
              <div className="flex gap-1">
                {workflow.languages.map((lang) => (
                  <Badge key={lang} variant="outline" className="text-xs">
                    {lang}
                  </Badge>
                ))}
              </div>
            ) : null}
          </div>
          {workflow?.description && (
            <p className="text-xs text-muted-foreground mt-1">
              {workflow.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {showTestButton && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onTest?.(binding.workflow_id, workflow?.name ?? binding.workflow_id)}
              aria-label={`Test workflow ${binding.workflow_id}`}
            >
              Test
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => removeWorkflow(binding.workflow_id)}
            aria-label={`Remove workflow ${binding.workflow_id}`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div>
        <label
          htmlFor={`channel-select-${binding.workflow_id}`}
          className="text-xs text-muted-foreground block mb-1"
        >
          Channel for {binding.workflow_id}
        </label>
        <select
          id={`channel-select-${binding.workflow_id}`}
          aria-label={`Channel for ${binding.workflow_id}`}
          value={binding.autostart_on_channel}
          onChange={(e) =>
            setWorkflowChannel(
              binding.workflow_id,
              e.target.value as WorkflowChannel,
            )
          }
          className="w-full rounded-md border border-border px-2 py-1 text-sm bg-background"
        >
          {CHANNEL_OPTIONS.map((ch) => (
            <option key={ch} value={ch}>
              {CHANNEL_LABELS[ch]}
            </option>
          ))}
        </select>
      </div>
    </li>
  )
}

interface WorkflowsCardProps {
  /**
   * P4.T9: optional scroll-and-flash handler from the parent
   * Workshop page. When set, ExplainPopover renders a
   * "Jump to Tools card" button for rule 6 findings.
   */
  onJump?: (location: FindingLocation) => void
}

export function WorkflowsCard({ onJump }: WorkflowsCardProps = {}) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [testingWorkflow, setTestingWorkflow] = useState<{
    workflowId: string
    workflowName: string
  } | null>(null)
  const draft = useComposerStore((s) => s.draftBundle)
  const server = useComposerStore((s) => s.serverBundle)
  const isWorkflowDesignerVisual = useFeature('workflow_designer_visual')
  // Self-gate: the workflows router only registers under
  // workflow_engine — mirror ToolsCard's tool_framework gate. Hooks
  // still run unconditionally; the flag disables the query and
  // short-circuits the render below.
  const workflowEngineOn = useFeature('workflow_engine')
  const workflows = useWorkflows({ enabled: workflowEngineOn })
  const findings = useBundleFindings()

  // bundleId from server bundle (stable identity after load)
  const bundleId = server?.id ?? draft?.id ?? ''

  const base = draft ?? server
  const bindings = ((base?.workflow_bindings ?? []) as WorkflowBindingValue[])

  const workflowMap = useMemo(
    () =>
      new Map<string, WorkflowSummary>(
        (workflows.data ?? []).map((w) => [w.id, w]),
      ),
    [workflows.data],
  )

  const sortedBindings = useMemo(
    () =>
      [...bindings].sort((a, b) => a.workflow_id.localeCompare(b.workflow_id)),
    [bindings],
  )

  // P4.T9: anchorId for workflow findings is binding.workflow_id.
  const findingForWorkflow = (wfId: string): Finding | undefined =>
    findings.find(
      (f) => f.location.card === 'workflows' && f.location.anchorId === wfId,
    )

  if (!workflowEngineOn) return null

  return (
    <Card className="p-6 gap-0">
      <h2 className="text-lg font-semibold mb-2">Workflows</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Multi-step processes the persona can run end-to-end. Install a template,
        choose a channel, and Save to publish the binding to the persona.
      </p>

      {workflows.isError && (
        <Alert variant="destructive" className="text-xs mb-4">
          <div className="flex items-center justify-between gap-3">
            <span>
              Unable to load the workflow catalog — display names + descriptions won't render.
              Removal + channel changes still work.
            </span>
            <Button variant="ghost" size="sm" onClick={() => workflows.refetch()}>
              Retry
            </Button>
          </div>
        </Alert>
      )}

      {sortedBindings.length === 0 ? (
        <p className="text-sm text-muted-foreground italic mb-4">
          No workflows installed. Click + Add workflow to choose a template.
        </p>
      ) : (
        <ul className="space-y-3 mb-4">
          {sortedBindings.map((binding) => (
            <BindingRow
              key={binding.workflow_id}
              binding={binding}
              workflow={workflowMap.get(binding.workflow_id)}
              finding={findingForWorkflow(binding.workflow_id)}
              onJump={onJump}
              showTestButton={isWorkflowDesignerVisual}
              onTest={(wfId, wfName) =>
                setTestingWorkflow({ workflowId: wfId, workflowName: wfName })
              }
            />
          ))}
        </ul>
      )}

      <Button variant="secondary" size="sm" onClick={() => setPickerOpen(true)}>
        + Add workflow
      </Button>

      <WorkflowPickerModal open={pickerOpen} onOpenChange={setPickerOpen} />

      {testingWorkflow && bundleId && (
        <WorkflowTestDrawer
          bundleId={bundleId}
          workflowId={testingWorkflow.workflowId}
          workflowName={testingWorkflow.workflowName}
          onClose={() => setTestingWorkflow(null)}
        />
      )}
    </Card>
  )
}
